import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable } from "mobx";
import type { GetLayoutFieldType } from "@whatslly/types/GetLayoutOptionsResponse";
import type { RecordTypeInfo } from "@whatslly/types/SfGetDescribeResponse";
import type { LayoutFieldType } from "@whatslly/types/LayoutFieldType";
import cloneDeep from "lodash/cloneDeep";
import orderBy from "lodash/orderBy";
import type { LayoutFieldsSetupBaseNamesType, LayoutFieldsSetupNamesType, LayoutFieldsSetupType } from "@whatslly/types/LayoutFieldsSetupType";
import { IPromiseBasedObservable, fromPromise } from "mobx-utils/lib/from-promise";
import { i18n } from "@whatslly/commons/i18nService";
import type { SelectOptionSingleType } from "../editLayoutUtils";
import { getDisplayOptions } from "../editLayoutUtils";
import { FormattedRecordType } from "@whatslly/commons/utilClasses/FormattedRecordType";
import { DATE_AND_TIME_FIELDS_API_NAMES } from "@whatslly/commons/constants";
import type { LayoutFieldsSettingsType } from "@whatslly/types/User";
import { LayoutFieldsProviderSymbol } from "../../../../TuvisSettings/stores/LayoutFieldsProviderSymbol";
import type { ILayoutFieldsProvider, LayoutFieldsFilter } from "../../../../TuvisSettings/types/ILayoutFieldsProvider";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UserStore } from "@whatslly/widget/stores/UserStore";

type EditLayoutContext = "lead" | "contact" | "account" | "personAccount" | undefined;
type RecordTypesByEntity = Record<string, FormattedRecordType[]>;
const orderFieldList = (layoutFieldTypes: LayoutFieldType[]) => {
  return orderBy(layoutFieldTypes, ["d", "r"], ["asc", "desc"]);
};

@injectable()
export class LayoutStore {
  lead: GetLayoutFieldType[] = [];
  case: GetLayoutFieldType[] = [];
  newLead: GetLayoutFieldType[] = [];
  contact: GetLayoutFieldType[] = [];
  newContact: GetLayoutFieldType[] = [];
  account: GetLayoutFieldType[] = [];
  newAccount: GetLayoutFieldType[] = [];
  newTask: GetLayoutFieldType[] = [];
  newCase: GetLayoutFieldType[] = [];
  task: GetLayoutFieldType[] = [];
  user: GetLayoutFieldType[] = [];
  manager: GetLayoutFieldType[] = [];
  wapTask: GetLayoutFieldType[] = [];
  customObjectMap = new Map<string, { fields: GetLayoutFieldType[]; recordTypes?: RecordTypeInfo[] }>();
  activeContext?: EditLayoutContext;
  opportunity: GetLayoutFieldType[] = [];
  newEvent: GetLayoutFieldType[] = [];
  event: GetLayoutFieldType[] = [];
  newPersonAccount: GetLayoutFieldType[] = [];
  personAccount: GetLayoutFieldType[] = [];
  activity: GetLayoutFieldType[] = [];
  newActivity: GetLayoutFieldType[] = [];
  private _recordTypesByEntity: RecordTypesByEntity = {};
  fieldsClone: LayoutFieldsSetupType = {} as LayoutFieldsSetupType;
  private allLayouts: SelectOptionSingleType[] = [];
  selectedLayout: SelectOptionSingleType = { label: "", value: "lead" };
  loading = false;
  selectedRecordType: FormattedRecordType | undefined;
  selectedLayoutAllFields: GetLayoutFieldType[] = [];
  initiated = false;
  private refreshFieldsAndSettings = false;
  private _filter?: LayoutFieldsFilter;
  initialLayoutFields?: LayoutFieldsSetupType;
  constructor(
    @inject(LayoutFieldsProviderSymbol) private layoutFieldsProvider: ILayoutFieldsProvider,
    @inject(InjectUserStore) protected userStore: UserStore
  ) {
    makeObservable(this, {
      lead: observable,
      newLead: observable,
      contact: observable,
      newContact: observable,
      account: observable,
      newAccount: observable,
      newTask: observable,
      newCase: observable,
      customObjectMap: observable,
      newPersonAccount: observable,
      personAccount: observable,
      activeContext: observable,
      task: observable,
      case: observable,
      opportunity: observable,
      newEvent: observable,
      event: observable,
      activity: observable,
      newActivity: observable,
      selectedLayout: observable.deep,
      loading: observable,
      selectedLayoutAllFields: observable,
      refreshFieldsAndSettings: observable,
      selectedRecordType: observable.deep,
      allLayouts: observable.deep,
      fieldsClone: observable.deep,
      _recordTypesByEntity: observable.deep,
      initialLayoutFields: observable,
      _filter: observable.shallow,
      setEntityFields: action,
      setFieldsClone: action,
      init: action,
      setSelectedRecordType: action,
      refreshStoreFieldsAndSettings: action,
      recordTypesByEntity: computed,
      fieldsAndSettings: computed,
      type: computed,
      currentFieldsSetup: computed,
      currentKey: computed,
      needToSave: computed,
      defaultRecordTypeDevName: computed,
      currentDraggableFieldsSetup: computed,
      currentNotDraggableFieldsSetup: computed,
      allowEditLayoutDragAndDrop: computed,
      loginProvider: computed,
      settings: computed,
      addFieldToFieldsClone: action,
      deleteFieldToFieldsClone: action,
      updateRequiredToFieldsClone: action,
      setSelectedLayoutAllFields: action,
      setLoading: action,
      setSelectedLayout: action,
      setFilters: action,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  get loginProvider() {
    return this.layoutFieldsProvider.loginProvider;
  }

  get settings() {
    const settingsFromPromise = this.fieldsAndSettings;
    if (settingsFromPromise?.state === "fulfilled") return settingsFromPromise.value.settings;
    return undefined;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  refreshStoreFieldsAndSettings() {
    this.refreshFieldsAndSettings = !this.refreshFieldsAndSettings;
  }

  async save(data: { fields: LayoutFieldsSetupType }): Promise<void> {
    await this.layoutFieldsProvider.saveLayoutFields({
      fields: data.fields,
      profileName: this._filter?.profileName,
    });
    this.setFieldsClone(data.fields, true);
  }

  get allowEditLayoutDragAndDrop() {
    return this.settings?.allowEditLayoutDragAndDrop;
  }

  setInitiated(newState: boolean) {
    this.initiated = newState;
  }

  async init(initialLayoutToUse?: string) {
    this.setLoading(true);
    this.allLayouts = getDisplayOptions(i18n.t, this.settings);
    const selectedLayout = this.allLayouts.find((layout) => layout.value === initialLayoutToUse) || this.allLayouts[0];
    this.setSelectedLayout(selectedLayout);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await this.getFields(selectedLayout?.value as any);
    this.setLoading(false);
    this.setInitiated(true);
  }

  setSelectedLayout(layout: SelectOptionSingleType) {
    this.selectedLayout = layout;
    this.selectedRecordType = undefined;
  }

  private setEntityFields = (
    type: LayoutFieldsSetupBaseNamesType,
    fields: GetLayoutFieldType[],
    recordTypes?: RecordTypeInfo[],
    typeEntityDescribeKey?: LayoutFieldsSetupBaseNamesType
  ) => {
    this[type] = fields;

    const clonedRecordTypes = { ...this.recordTypesByEntity };
    if (recordTypes) {
      clonedRecordTypes[type] = this.formatRecordTypes(recordTypes, type);
      if (type === "newTask") clonedRecordTypes["newActivity"] = this.formatRecordTypes(recordTypes, type);
    }

    if (typeEntityDescribeKey && typeEntityDescribeKey !== type) {
      this[typeEntityDescribeKey] = fields;
      if (recordTypes) clonedRecordTypes[typeEntityDescribeKey] = this.formatRecordTypes(recordTypes, type);
    }
    const clonedRecordTypesWithUnknowns = this.handleUnknownRecordTypes(type, clonedRecordTypes);
    this._recordTypesByEntity = clonedRecordTypesWithUnknowns;
  };

  setSelectedRecordType(recordType?: FormattedRecordType) {
    this.selectedRecordType = recordType;
  }

  getLayoutHasSelectedRecordType(keyToSearch: string): keyof LayoutFieldsSetupType | undefined {
    const layoutFieldsKeys = Object.keys(this.fieldsClone);
    const firstRecordType = layoutFieldsKeys.find((layoutKey) => layoutKey.startsWith(`${keyToSearch}:`));
    if (!firstRecordType) return;
    return firstRecordType.split(":")[1] as keyof LayoutFieldsSetupType | undefined;
  }

  async changeLayout(newValue: SelectOptionSingleType, handleCustomObjects: () => Promise<boolean | undefined>) {
    if (newValue?.value === this.selectedLayout?.value) return;

    this.setLoading(true);
    const layoutRecordTypeKey = this.getLayoutHasSelectedRecordType(newValue.value);
    this.selectedRecordType && !layoutRecordTypeKey && this.setSelectedRecordType(undefined);
    this.setSelectedLayout(newValue);
    const isCustomObject = await handleCustomObjects();
    this.layoutFieldsProvider.trk?.({
      action: "layout-field-changed",
    });
    if (isCustomObject) return this.setLoading(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = await this.getFields(this.type as any);
    this.setLoading(false);
    if (!res) {
      return;
    }

    if (layoutRecordTypeKey) {
      let defaultRecordToReturn = cloneDeep(
        this._recordTypesByEntity[this.type]?.find((recordType) => recordType.recordTypeSelector === this.defaultRecordTypeDevName)
      );
      if (this.defaultRecordTypeDevName === layoutRecordTypeKey) {
        defaultRecordToReturn = { recordTypeSelector: layoutRecordTypeKey } as any;
      }
      if (defaultRecordToReturn) {
        defaultRecordToReturn.recordTypeSelector = `${defaultRecordToReturn.recordTypeSelector}:default`;
      }

      const layoutRecordTypeValue = this._recordTypesByEntity[this.type]?.find((recordType) => recordType.recordTypeSelector === layoutRecordTypeKey);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.setSelectedRecordType(defaultRecordToReturn || layoutRecordTypeValue || ({ recordTypeSelector: layoutRecordTypeKey } as any));
    }

    const { fields } = res;
    this.setSelectedLayoutAllFields(fields || []);
  }

  get recordTypesByEntity() {
    return this._recordTypesByEntity;
  }

  setFieldsClone(fieldsClone: LayoutFieldsSetupType, refreshInitialLayoutFields?: boolean) {
    if (!this.initialLayoutFields || refreshInitialLayoutFields) {
      this.initialLayoutFields = fieldsClone;
    }
    this.fieldsClone = cloneDeep(fieldsClone);
  }

  private mutateNewEvent(fieldsToMutate: LayoutFieldsSetupType): LayoutFieldsSetupType {
    const newEventKeys = Object.keys(fieldsToMutate).filter((key) => key.startsWith("newEvent")) as (keyof LayoutFieldsSetupType)[];
    newEventKeys.forEach((key) => {
      fieldsToMutate[key] = fieldsToMutate[key].filter((field) => !DATE_AND_TIME_FIELDS_API_NAMES.includes(field.a));
      if (fieldsToMutate[key].some((field) => field.a === "EventDateTimePicker")) return;
      fieldsToMutate[key].unshift({ l: `Date and Time`, a: `EventDateTimePicker`, t: `eventDateTimePicker`, d: false, r: false });
    });
    return fieldsToMutate;
  }

  get fieldsAndSettings(): IPromiseBasedObservable<{ layout: LayoutFieldsSetupType; settings: LayoutFieldsSettingsType }> {
    if (this.refreshFieldsAndSettings) {
      // refresh
    }
    return fromPromise(
      this.layoutFieldsProvider.getFieldsAndSettings(this._filter, this.loginProvider).then((res) => {
        const resWithMutateNewEvent = this.mutateNewEvent(res.layout);
        this.setFieldsClone(resWithMutateNewEvent);
        this.setLoading(false);
        return { layout: res.layout, settings: res.settings };
      })
    );
  }

  get needToSave() {
    return this.fieldsAndSettings.state === "fulfilled" && this.initialLayoutFields
      ? JSON.stringify(this.fieldsClone) !== JSON.stringify(this.initialLayoutFields)
      : false;
  }

  get type() {
    return this.selectedLayout?.value;
  }

  get currentKey() {
    return this.selectedRecordType
      ? (`${this.type}:${this.selectedRecordType.recordTypeSelector}${
          this.selectedRecordType.isDefault ? ":default" : ""
        }` as keyof LayoutFieldsSetupType)
      : this.type;
  }

  get currentFieldsSetup() {
    return this.settings?.allowEditLayoutDragAndDrop ? this.fieldsClone[this.currentKey] : orderFieldList(this.fieldsClone[this.currentKey]);
  }

  get hasRecordTypes() {
    return this._recordTypesByEntity[this.type] && this._recordTypesByEntity[this.type].length > 0;
  }

  get defaultRecordTypeDevName() {
    return Object.keys(this.fieldsClone)
      .filter((layoutKey) => layoutKey.startsWith(this.type))
      .find((recordTypeKey) => recordTypeKey.includes("default"))
      ?.split(":")[1];
  }

  setSelectedLayoutAllFields(fields: GetLayoutFieldType[]) {
    this.selectedLayoutAllFields = fields;
  }

  addFieldToFieldsClone(field: GetLayoutFieldType) {
    const fieldsClone = { ...this.fieldsClone };
    if (!fieldsClone[this.currentKey]) fieldsClone[this.currentKey] = [];
    fieldsClone[this.currentKey].push({
      l: field.label,
      a: field.name,
      d: true,
      r: false,
      t: field.type,
      relationshipName: field.relationshipName,
      relationshipFieldName: field.relationshipFieldName,
    });
    this.setFieldsClone(fieldsClone);
  }

  addRawFieldToFieldsClone(newItem: LayoutFieldType) {
    const fieldsClone = { ...this.fieldsClone };
    if (!fieldsClone[this.currentKey]) fieldsClone[this.currentKey] = [];
    const itemIndex = fieldsClone[this.currentKey].findIndex((field) => field.a === newItem.a);
    if (itemIndex > -1) {
      fieldsClone[this.currentKey][itemIndex] = newItem;
    } else {
      fieldsClone[this.currentKey].push(newItem);
    }
    this.setFieldsClone(fieldsClone);
  }

  deleteFieldToFieldsClone(field: LayoutFieldType) {
    const fieldsClone = { ...this.fieldsClone };
    if (!fieldsClone[this.currentKey]) fieldsClone[this.currentKey] = [];

    const fieldIndex = fieldsClone[this.currentKey].findIndex((f) => f.a === field.a);
    if (fieldIndex === -1) return;

    fieldsClone[this.currentKey].splice(fieldIndex, 1);
    this.setFieldsClone(fieldsClone);
  }

  updateRequiredToFieldsClone(field: LayoutFieldType) {
    const fieldsClone = { ...this.fieldsClone };
    if (!fieldsClone[this.currentKey]) fieldsClone[this.currentKey] = [];

    const fieldIndex = fieldsClone[this.currentKey].findIndex((f) => f.a === field.a);
    if (fieldIndex === -1) return;

    fieldsClone[this.currentKey][fieldIndex].r = !field.r;
    this.setFieldsClone(fieldsClone);
  }

  updateItemsPosition(fields: LayoutFieldType[]) {
    const fieldsClone = { ...this.fieldsClone };
    if (!fieldsClone[this.currentKey]) fieldsClone[this.currentKey] = [];
    fieldsClone[this.currentKey] = fields;
    this.setFieldsClone(fieldsClone);
  }

  private getDefaultRecordTypeKeyAndFields(): { defaultRecordKey: LayoutFieldsSetupNamesType; defaultRecordData: LayoutFieldType[] } | undefined {
    const currentDefault = Object.entries(this.fieldsClone)
      .filter(([layoutKey]) => layoutKey.startsWith(this.type))
      .find(([layoutKey]) => layoutKey.includes("default"));
    if (!currentDefault) return;
    return { defaultRecordKey: currentDefault[0] as LayoutFieldsSetupNamesType, defaultRecordData: currentDefault[1] };
  }

  private getNewDefaultRecordTypeId(recordTypes: FormattedRecordType[]) {
    return recordTypes.find((recordType) => recordType.isDefault)?.recordTypeSelector;
  }

  private handleChangeDefaultRecordType(recordTypes: FormattedRecordType[]) {
    const copyLayoutFieldsSetup = cloneDeep(this.fieldsClone);
    const oldDefaultRecordType = this.getDefaultRecordTypeKeyAndFields();
    const newDefaultRecordTypeId = this.getNewDefaultRecordTypeId(recordTypes);
    if (!newDefaultRecordTypeId) return copyLayoutFieldsSetup;

    // create splited key to check if new default alrady have a type
    const newDefaultRecordTypeDataToSet =
      copyLayoutFieldsSetup[`${this.type}:${newDefaultRecordTypeId}`] ||
      copyLayoutFieldsSetup[`${this.type}:${newDefaultRecordTypeId}:default`] ||
      copyLayoutFieldsSetup[`${this.type}`];
    if (!oldDefaultRecordType) {
      copyLayoutFieldsSetup[`${this.type}:${newDefaultRecordTypeId}:default`] = newDefaultRecordTypeDataToSet;
    } else {
      const { defaultRecordKey, defaultRecordData } = oldDefaultRecordType;
      if (defaultRecordKey in copyLayoutFieldsSetup && defaultRecordKey !== `${this.type}:${newDefaultRecordTypeId}`) {
        // copy the old default record type and add it as no default
        const typescriptFixForCurrentDefaultKey = defaultRecordKey as keyof LayoutFieldsSetupType;
        delete copyLayoutFieldsSetup[defaultRecordKey];
        const splitedKey = typescriptFixForCurrentDefaultKey.split(":") as [LayoutFieldsSetupBaseNamesType, string];
        copyLayoutFieldsSetup[`${splitedKey[0]}:${splitedKey[1]}`] = defaultRecordData;

        copyLayoutFieldsSetup[`${this.type}:${newDefaultRecordTypeId}`] && delete copyLayoutFieldsSetup[`${this.type}:${newDefaultRecordTypeId}`];
        // create new default record type
        copyLayoutFieldsSetup[`${this.type}:${newDefaultRecordTypeId}:default`] = newDefaultRecordTypeDataToSet;
      }
    }
    this.setSelectedRecordType(recordTypes.find((recordType) => recordType.isDefault) || recordTypes[0]);
    return copyLayoutFieldsSetup;
  }

  private formatUnknownRecordTypesKeys(recordTypesDevName: string[], type: LayoutFieldsSetupNamesType) {
    const formattedAsSfRes = recordTypesDevName.map((recordTypeKey) => ({
      active: true,
      available: true,
      defaultRecordTypeMapping: false,
      developerName: recordTypeKey,
      master: false,
      name: recordTypeKey,
      recordTypeId: "undefined",
    }));
    return formattedAsSfRes.map((recordType) => new FormattedRecordType(recordType, type, Object.keys(this.fieldsClone)));
  }

  private handleUnknownRecordTypes(type: LayoutFieldsSetupNamesType, currentRecordTypesByEntity: RecordTypesByEntity) {
    const onlySelectedTypeRecordTypes = Object.keys(this.fieldsClone).filter((key) => key.includes(type) && key.includes(":") && key !== type);
    const recordTypesDevName = onlySelectedTypeRecordTypes.map((key) => key.split(":")[1]);
    const formattedRecordTypes = this.formatUnknownRecordTypesKeys(recordTypesDevName, type);
    const currentEntityRecordTypesSelectors =
      currentRecordTypesByEntity[type]?.map((formmatedRecordType) => formmatedRecordType.recordTypeSelector) || [];

    const unknownRecordTypes = formattedRecordTypes.filter(
      (formmatedUnknownRecordType) => !currentEntityRecordTypesSelectors.includes(formmatedUnknownRecordType.recordTypeSelector)
    );
    currentRecordTypesByEntity[type] = [...(currentRecordTypesByEntity[type] || []), ...unknownRecordTypes];
    return currentRecordTypesByEntity;
  }

  formatRecordTypes(recordTypes: (RecordTypeInfo | FormattedRecordType)[], entity: LayoutFieldsSetupNamesType) {
    return recordTypes.map((recordType) => new FormattedRecordType(recordType, entity, Object.keys(this.fieldsClone)));
  }

  updateSelectedRecordTypes(recordTypes: FormattedRecordType[]) {
    const formattedRecordTypes = this.formatRecordTypes(recordTypes, this.type);
    const copyLayoutFieldsSetupAndHandleDefault = this.handleChangeDefaultRecordType(formattedRecordTypes);

    const newLayoutFieldsSetupFilter = Object.entries(copyLayoutFieldsSetupAndHandleDefault).filter(([typeName]) => {
      if (!typeName.startsWith(this.type) || typeName === this.type) return true;
      return Boolean(formattedRecordTypes.find((recordType) => typeName.includes(`${this.type}:${recordType.recordTypeSelector}`)));
    });

    const newLayoutFieldsSetup = Object.fromEntries(newLayoutFieldsSetupFilter);
    const recordTypesToAdd = formattedRecordTypes.reduce<Partial<LayoutFieldsSetupType>>((acc, recordType) => {
      if (
        newLayoutFieldsSetup[`${this.type}:${recordType.recordTypeSelector}`] ||
        newLayoutFieldsSetup[`${this.type}:${recordType.recordTypeSelector}:default`]
      ) {
        return acc;
      }
      acc[`${this.type}:${recordType.recordTypeSelector}`] = cloneDeep(newLayoutFieldsSetup[this.type]);
      return acc;
    }, {});

    const newLayoutFieldsSetupToUse = { ...newLayoutFieldsSetup, ...recordTypesToAdd };
    this.setFieldsClone(newLayoutFieldsSetupToUse as LayoutFieldsSetupType);
    if (formattedRecordTypes.length === 0) return this.setSelectedRecordType(undefined);

    const currentSelectedRecordTypeDeleted = !formattedRecordTypes.find((recordType) => {
      return recordType.recordTypeSelector.includes(this.selectedRecordType?.recordTypeSelector || "");
    });
    if (currentSelectedRecordTypeDeleted) this.setSelectedRecordType(formattedRecordTypes[0]);
  }

  addDefaultRecordTypeIfMissing(type: LayoutFieldsSetupBaseNamesType, filteredFieldsSetup: LayoutFieldsSetupType) {
    const selectedTypeRecordLayoutKeys = Object.keys(filteredFieldsSetup).filter((layoutKey) =>
      layoutKey.startsWith(`${type}:`)
    ) as LayoutFieldsSetupNamesType[];
    if (selectedTypeRecordLayoutKeys.length === 0) return;

    const defaultRecordTypeKey = selectedTypeRecordLayoutKeys.find((recordTypeLayoutKey) => recordTypeLayoutKey.endsWith(":default"));
    if (defaultRecordTypeKey) return;

    const newDefaultType = selectedTypeRecordLayoutKeys[0];
    const newDefaultTypeData = filteredFieldsSetup[newDefaultType];
    const fieldsSetupEntriesWithOutNewDefault = Object.entries(filteredFieldsSetup).filter(([layoutKey]) => layoutKey !== newDefaultType);
    const newFieldsSetupToSet = {
      ...Object.fromEntries(fieldsSetupEntriesWithOutNewDefault),
      ...{ [`${newDefaultType}:default`]: newDefaultTypeData },
    } as LayoutFieldsSetupType;

    this.setFieldsClone(newFieldsSetupToSet);
  }

  private replaceTypeToSaveDuplicatedRequests(type: LayoutFieldsSetupBaseNamesType) {
    switch (type) {
      case "activity":
        return "task";
      case "newActivity":
        return "newTask";
      default:
        return type;
    }
  }

  async getFields(type?: LayoutFieldsSetupBaseNamesType) {
    if (!type) return;
    const typeEntityDescribeKey = this.replaceTypeToSaveDuplicatedRequests(type);
    if (Array.isArray(this[typeEntityDescribeKey]) && this.recordTypesByEntity[typeEntityDescribeKey]) {
      this.setSelectedLayoutAllFields(this[typeEntityDescribeKey]);
      return { fields: this[typeEntityDescribeKey], recordTypes: this.recordTypesByEntity[typeEntityDescribeKey] };
    }
    const res = await this.layoutFieldsProvider.getLayoutFieldOptions({ type: typeEntityDescribeKey }, this.loginProvider);
    if (!res?.success) return;
    this.addDefaultRecordTypeIfMissing(typeEntityDescribeKey, this.fieldsClone);

    this.setSelectedLayoutAllFields(res.fields);
    this.setEntityFields(type, res.fields, res.recordTypes, typeEntityDescribeKey);
    return { fields: this[typeEntityDescribeKey], recordTypes: res.recordTypes };
  }

  async getCustomObjectFields(customObjectKey: string, cacheKey: string, isUpdatable: boolean) {
    if (Array.isArray(this.customObjectMap.get(cacheKey))) {
      return this.customObjectMap.get(cacheKey);
    }

    const res = await this.layoutFieldsProvider.getCustomObjectFields(customObjectKey, isUpdatable, this.loginProvider);
    if (!res?.success) {
      return;
    }
    this.customObjectMap.set(cacheKey, { fields: res.fields, recordTypes: res.recordTypes });
    this.setEntityFields(cacheKey as any, res.fields, res.recordTypes, cacheKey as any);
    return { fields: res.fields, recordTypes: res.recordTypes };
  }

  setActiveContext(type: EditLayoutContext) {
    this.activeContext = type;
  }

  getActiveContext() {
    return this.activeContext;
  }

  get currentDraggableFieldsSetup() {
    return this.currentFieldsSetup?.filter((field) => field.d && this.settings?.allowEditLayoutDragAndDrop);
  }

  get currentNotDraggableFieldsSetup() {
    return this.currentFieldsSetup?.filter((field) => !field.d || !this.settings?.allowEditLayoutDragAndDrop);
  }

  setFilters(filters?: LayoutFieldsFilter) {
    if (filters?.profileName !== this._filter?.profileName) {
      this.initialLayoutFields = undefined;
    }
    this._filter = filters;
  }
}
